import { createContext, useContext, useEffect, useMemo, useState } from "react";
import i18n from "../i18n";

/**
 * LanguageContext — single source of truth for app language.
 *
 * Side effects on mount and on every language change:
 *   • i18next is told to switch ("changeLanguage")
 *   • <html lang="..."> and <html dir="rtl|ltr"> are updated so global
 *     CSS, screen readers, and text rendering all align
 *   • A `lang-en` / `lang-he` class is toggled on <body> to give
 *     specific components an easy CSS hook (e.g. notebook spine flips)
 *   • Choice is persisted to localStorage under "torahya_lang"
 *
 * Defaults to Hebrew unless the user previously chose English.
 */
const LanguageCtx = createContext(null);

const STORAGE_KEY = "torahya_lang";

function getInitial() {
  if (typeof window === "undefined") return "he";
  // URL `?lang=` wins when explicitly set — lets us send shareable
  // links like `torahya.com/?lang=en` straight into English without
  // touching anyone's saved preference. The value is then persisted
  // to localStorage by the mount effect, so subsequent visits without
  // the param keep the same language.
  //   • ?lang=en → English
  //   • ?lang=he → Hebrew
  //   • anything else (or no param at all) → fall through to saved
  //     preference (and ultimately to the Hebrew default). This is
  //     critical: an existing user who saved Hebrew must not get
  //     flipped to EN by a stray link without a `?lang=` param.
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get("lang");
    if (fromUrl === "en" || fromUrl === "he") return fromUrl;
  } catch { /* malformed URL — fall through */ }
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved === "en" || saved === "he") return saved;
  return "he"; // default — never auto-detect, sacred app must stay Hebrew unless explicitly switched
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(getInitial);

  useEffect(() => {
    const dir = lang === "en" ? "ltr" : "rtl";
    const html = document.documentElement;
    const body = document.body;
    html.setAttribute("lang", lang);
    html.setAttribute("dir", dir);
    body.classList.remove("lang-he", "lang-en");
    body.classList.add(`lang-${lang}`);
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    // Keep <title> + meta tags in sync with the active language so
    // anyone who shares the tab (or revisits via browser history)
    // sees the correct language in their tab strip and previews.
    // Same texts as the inline pre-mount script in index.html — kept
    // here for the post-mount language-toggle path. Hebrew remains the
    // shipped default; the EN strings are the spec-mandated copy.
    try {
      const HE_TITLE = "תוריא · Torahya";
      const HE_DESC  = "בית המדרש הדיגיטלי לכל יהודי יקר. לימוד עם פרשנים, בניית שיעור, מחברת חכמה וחברותא ללימוד.";
      const EN_TITLE = "Torahya · תוריא";
      const EN_DESC  = "Torahya — Your Personal Beit Midrash. Learn Torah, Mishnah, Talmud with classic commentaries, Chavruta learning, and personal notebook.";
      const isEn = lang === "en";
      const title = isEn ? EN_TITLE : HE_TITLE;
      const desc  = isEn ? EN_DESC  : HE_DESC;
      document.title = title;
      const setAttr = (sel, attr, val) => {
        const el = document.querySelector(sel);
        if (el) el.setAttribute(attr, val);
      };
      setAttr('meta[name="description"]',        "content", desc);
      setAttr('meta[property="og:title"]',        "content", title);
      setAttr('meta[property="og:description"]',  "content", desc);
      setAttr('meta[property="og:locale"]',       "content", isEn ? "en_US" : "he_IL");
      setAttr('meta[property="og:locale:alternate"]', "content", isEn ? "he_IL" : "en_US");
      setAttr('meta[name="twitter:title"]',       "content", title);
      setAttr('meta[name="twitter:description"]', "content", desc);
      setAttr('meta[name="apple-mobile-web-app-title"]', "content", isEn ? "Torahya" : "תוריא");
    } catch { /* DOM not ready or sandboxed */ }
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage may be blocked — ignore */
    }
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang: setLangState,
      toggle: () => setLangState((p) => (p === "he" ? "en" : "he")),
      isHe: lang === "he",
      isEn: lang === "en",
      dir: lang === "en" ? "ltr" : "rtl",
    }),
    [lang]
  );

  return <LanguageCtx.Provider value={value}>{children}</LanguageCtx.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageCtx);
  if (!ctx) throw new Error("useLanguage must be used within <LanguageProvider>");
  return ctx;
}
